import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useGoalMasteryReport,
    useExportGoalMastery,
    type GoalMasteryFilter,
} from '@/api/reports';
import { useClientNames } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
/**
 * Goal Mastery — per-client treatment-plan progress.
 *
 * Two views in one page:
 *   - Snapshot: counts per status per client, % mastered. Always reflects
 *     current state regardless of date filter.
 *   - Cohort: "Mastered in window" column + KPI, scoped by date range.
 *
 * When a single client is selected, a per-target table appears below the
 * rollup so a BCBA can see the actual targets behind the numbers — useful
 * for reauth packets and parent updates.
 */
export function GoalMasteryPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<GoalMasteryFilter>({});
    const [draft, setDraft]   = useState<GoalMasteryFilter>({});

    const { data, isLoading, isError, error, isFetching } = useGoalMasteryReport(filter);
    const exportM = useExportGoalMastery();
    const clientsQuery = useClientNames();
    const staffQuery   = useUserDirectory();

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [];
        return clientsQuery.data
            .filter((c) => c.status === 'Active')
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    const bcbaOptions = useMemo(() => {
        if (!staffQuery.data) return [];
        return staffQuery.data
            .filter((s) => s.roleType === 'BCBA')
            .map((s) => ({ id: s.id, label: `${s.last}, ${s.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [staffQuery.data]);

    function apply() { setFilter({ ...draft }); }
    function clear() { setDraft({}); setFilter({}); }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* surfaced below */ }
    }

    const hasWindow = !!(filter.dateFrom || filter.dateTo);

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Goal Mastery</h1>
                    <p className="text-xs text-text-subtle">
                        Per-client treatment-plan progress. Snapshot reflects current state; the date window scopes the &ldquo;Mastered in window&rdquo; cohort metric only.
                    </p>
                </div>
                {canExport && (
                    <Button variant="primary" size="sm" onClick={runExport} disabled={exportM.isPending || isLoading}>
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <label className="text-xs block">
                            <span className="text-text-muted">Client</span>
                            <select
                                value={draft.clientId ?? ''}
                                onChange={(e) => setDraft({ ...draft, clientId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All clients</option>
                                {clientOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">BCBA</span>
                            <select
                                value={draft.bcbaId ?? ''}
                                onChange={(e) => setDraft({ ...draft, bcbaId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All BCBAs</option>
                                {bcbaOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Mastered from</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Mastered to</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn&apos;t load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className={`grid gap-3 ${hasWindow ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'}`}>
                        <Kpi label="Clients" value={data.kpis.totalClients.toLocaleString()} />
                        <Kpi label="Targets" value={data.kpis.totalTargets.toLocaleString()} />
                        <Kpi label="Mastered (all-time)" value={data.kpis.mastered.toLocaleString()} />
                        <Kpi label="In progress" value={data.kpis.inProgress.toLocaleString()} />
                        {hasWindow && <Kpi label="Mastered in window" value={data.kpis.masteredInWindow.toLocaleString()} />}
                    </div>

                    <Card>
                        <CardHeader>
                            <div>
                                <CardTitle>Per client</CardTitle>
                                <CardSubtitle>
                                    % Mastered uses (Mastered / non-discontinued targets); discontinued targets are excluded from the denominator.
                                </CardSubtitle>
                            </div>
                        </CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.clients.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No targets match the current filter.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Client</th>
                                            <th className="px-3 py-2 font-bold">BCBA</th>
                                            <th className="px-3 py-2 font-bold text-right">Total</th>
                                            <th className="px-3 py-2 font-bold text-right">In progress</th>
                                            <th className="px-3 py-2 font-bold text-right">On hold</th>
                                            <th className="px-3 py-2 font-bold text-right">Mastered</th>
                                            {hasWindow && <th className="px-3 py-2 font-bold text-right">In window</th>}
                                            <th className="px-3 py-2 font-bold text-right">Discontinued</th>
                                            <th className="px-3 py-2 font-bold text-right">% Mastered</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.clients.map((r) => (
                                            <tr key={r.client_id} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{r.client_name}</td>
                                                <td className="px-3 py-2 text-xs text-text-muted">{r.bcba_name || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.total}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.in_progress}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.on_hold}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.mastered}</td>
                                                {hasWindow && <td className="px-3 py-2 text-right tabular-nums text-text-muted">{r.mastered_in_window}</td>}
                                                <td className="px-3 py-2 text-right tabular-nums text-text-muted">{r.discontinued}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-medium">{r.mastery_rate_pct}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>

                    {filter.clientId && data.targets && (
                        <Card>
                            <CardHeader>
                                <div>
                                    <CardTitle>Targets</CardTitle>
                                    <CardSubtitle>
                                        Every target on the selected client&apos;s plan, current status, and mastery date when applicable.
                                    </CardSubtitle>
                                </div>
                            </CardHeader>
                            <CardBody className="pt-0 overflow-x-auto">
                                {data.targets.length === 0 ? (
                                    <p className="text-xs text-text-subtle px-3 py-4">No targets on this client&apos;s plan.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-3 py-2 font-bold">Program</th>
                                                <th className="px-3 py-2 font-bold">Target</th>
                                                <th className="px-3 py-2 font-bold">Type</th>
                                                <th className="px-3 py-2 font-bold">Mastery criterion</th>
                                                <th className="px-3 py-2 font-bold">Status</th>
                                                <th className="px-3 py-2 font-bold">Mastered on</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.targets.map((t) => (
                                                <tr key={t.targetId} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2 text-xs text-text-muted">{t.programName}</td>
                                                    <td className="px-3 py-2">{t.name}</td>
                                                    <td className="px-3 py-2 text-xs text-text-muted">{t.dataType}</td>
                                                    <td className="px-3 py-2 text-xs tabular-nums">
                                                        {t.masteryValue != null
                                                            ? `${t.masteryDirection === 'at_or_above' ? '≥' : '≤'} ${t.masteryValue}${unitFor(t.dataType)}`
                                                            : '—'}
                                                    </td>
                                                    <td className="px-3 py-2"><StatusBadge status={t.status} /></td>
                                                    <td className="px-3 py-2 text-xs tabular-nums text-text-muted">
                                                        {t.masteredAt ? t.masteredAt.slice(0, 10) : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardBody>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}

function unitFor(dataType: string): string {
    if (dataType === 'duration') return 's';
    if (dataType === 'frequency') return '';
    return '%';
}

function Kpi({ label, value }: { label: string; value: string }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className="font-display text-xl font-bold mt-1 tabular-nums text-text">{value}</p>
            </CardBody>
        </Card>
    );
}
